// /app/frontend/src/pages/ChoralIntelligenceDemo.jsx
//
// Public, no-auth acceptance surface for Checkpoint 7.
// Renders the Christ the King · Year A fixture end-to-end against the
// live 290-work catalog with a seeded demo parish history so the
// component scores, "Because …" explanations, and cautions are all
// visible on real evidence — not mocks.

import React, { useEffect, useState } from "react";
import ChoralRecommendationCard from "@/features/choral-intelligence/ChoralRecommendationCard";
import "@/features/choral-intelligence/choral-intelligence.css";

const API = process.env.REACT_APP_BACKEND_URL;

function ChoralRecommendationResults({ recommendations }) {
  if (!recommendations?.length) {
    return (
      <section className="ci-empty" role="status" data-testid="ci-empty-state">
        <h2 className="ci-empty__title">
          No governed choral recommendation is available
        </h2>
        <p>
          The catalog does not currently contain a work that satisfies
          the selected liturgical and parish constraints.
        </p>
        <p className="ci-empty__secondary">
          No substitute title has been generated.
        </p>
      </section>
    );
  }
  return (
    <div className="ci-results" data-testid="ci-results-list">
      {recommendations.map((recommendation) => (
        <ChoralRecommendationCard
          key={recommendation.work.id}
          recommendation={recommendation}
        />
      ))}
    </div>
  );
}

export default function ChoralIntelligenceDemo() {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(
          `${API}/api/musical-intelligence/choral/recommend/demo/christ-the-king-year-a`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message, data: null });
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--li-parchment, #F1E7CE)",
        padding: "48px 20px 96px",
      }}
      data-testid="ci-demo-page"
    >
      <header
        style={{
          maxWidth: 900,
          margin: "0 auto 40px",
          borderBottom: "1px solid var(--li-border, #C7BDA6)",
          paddingBottom: 32,
        }}
      >
        <p
          style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: "0.72rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--li-text-secondary, #5C5450)",
            margin: 0,
          }}
        >
          Choral Intelligence™ · Acceptance Fixture
        </p>
        <h1
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.05,
            margin: "12px 0 6px",
            color: "var(--li-text-primary, #2C231C)",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          Christ the King · Year&nbsp;A
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "0.98rem",
            lineHeight: 1.6,
            color: "var(--li-text-secondary, #5C5450)",
            maxWidth: 640,
            margin: "12px 0 0",
          }}
        >
          Offertory · Ezekiel 34 · Psalm 23 · Matthew 25 · themes of
          kingship, shepherd, justice, mercy, and kingdom. Every
          recommendation below is a real, governed catalog record
          ranked deterministically over retrieved works — with Parish
          Memory and repertoire freshness applied.
        </p>
      </header>

      <main style={{ maxWidth: 900, margin: "0 auto" }}>
        {state.loading && (
          <p
            data-testid="ci-loading"
            style={{
              fontFamily: '"IBM Plex Mono", monospace',
              color: "var(--li-text-secondary, #5C5450)",
              textAlign: "center",
              padding: 48,
            }}
          >
            Retrieving governed candidates…
          </p>
        )}
        {state.error && (
          <p
            data-testid="ci-error"
            style={{
              fontFamily: "Inter, sans-serif",
              color: "#8A5A32",
              textAlign: "center",
              padding: 48,
            }}
          >
            Unable to load acceptance fixture: {state.error}
          </p>
        )}
        {state.data && (
          <ChoralRecommendationResults
            recommendations={state.data.recommendations}
          />
        )}
      </main>
    </div>
  );
}
